import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { useTerritorio } from "../../context/TerritorioContext";
import { useOnlineStatus, leerBacVisitasCache } from "../../hooks/useSync";
import { getVisitasBac } from "../api/bacSarampionAPI.js";
import { coincideJornada } from "../jornadas/SelectorJornada";

const VERDE = "#16342a";
const VERDE_CLARO = "#2d6a4f";
const NARANJA = "#c97b4a";
const ROJO = "#b3261e";
const AMARILLO = "#b7791f";
const GRIS = "#8b95a1";
const PALETA = ["#16342a", "#2d6a4f", "#c97b4a", "#b7791f", "#b3261e", "#4d8f6f", "#8b95a1", "#e6b800", "#6c757d", "#0C447C"];

const ESTADO_COLOR = { Efectiva: VERDE_CLARO, Cerrada: AMARILLO, Renuente: ROJO, "No habitada": GRIS };

const CARD = { background: "#fff", borderRadius: 12, border: "1px solid #DCE3DE", padding: "16px 18px" };
const TITULO_CARD = { fontSize: 14, fontWeight: 700, color: "#0F3B2E", marginBottom: 14 };

function bucketEdad(edad, unidad) {
  const n = parseInt(edad, 10);
  if (isNaN(n)) return "Sin dato";
  const anios = unidad === "Meses" ? n / 12 : n;
  if (anios < 1) return "< 1 año";
  if (anios <= 4) return "1-4";
  if (anios <= 14) return "5-14";
  if (anios <= 24) return "15-24";
  if (anios <= 34) return "25-34";
  if (anios <= 49) return "35-49";
  if (anios <= 64) return "50-64";
  return "65+";
}
const ORDEN_EDAD = ["< 1 año", "1-4", "5-14", "15-24", "25-34", "35-49", "50-64", "65+", "Sin dato"];

const aArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

function contarPor(lista, campo) {
  const mapa = {};
  lista.forEach(v => {
    const val = v[campo];
    if (!val) return;
    mapa[val] = (mapa[val] || 0) + 1;
  });
  return Object.entries(mapa).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

export default function DashboardBacSarampion({ filtroJornada = "" }) {
  const { data: ctx } = useTerritorio();
  const online = useOnlineStatus();
  const [visitasBase, setVisitas] = useState([]);
  // Dentro de una jornada, el tablero se calcula solo con sus visitas
  const visitas = useMemo(() => visitasBase.filter(v => coincideJornada(v.jornada_codigo, filtroJornada)), [visitasBase, filtroJornada]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      if (!ctx?.territorio) { setVisitas([]); setCargando(false); return; }
      setCargando(true);
      try {
        if (online) {
          const res = await getVisitasBac({ territorio: ctx.territorio, comunidad: ctx.comunidad });
          setVisitas(res.data || []);
        } else {
          setVisitas(await leerBacVisitasCache(ctx.territorio, ctx.comunidad));
        }
      } catch {
        setVisitas(await leerBacVisitasCache(ctx.territorio, ctx.comunidad));
      } finally {
        setCargando(false);
      }
    };
    cargar();
    const handler = () => cargar();
    window.addEventListener("sync-completado", handler);
    return () => window.removeEventListener("sync-completado", handler);
  }, [ctx?.territorio, ctx?.comunidad, online]);

  // ── Última visita por vivienda (para KPIs de estado real) ─────────────
  const porVivienda = useMemo(() => {
    const mapa = new Map();
    for (const v of visitas) {
      const actual = mapa.get(v.codigo_vivienda);
      if (!actual || (v.numero_visita || 0) >= (actual.numero_visita || 0)) mapa.set(v.codigo_vivienda, v);
    }
    return [...mapa.values()];
  }, [visitas]);

  // Reconoce "Renuente" en las 2 formas que puede venir guardado: la
  // vieja (estado_vivienda = "Renuente" directo) y la nueva
  // (estado_vivienda = "No efectiva" con motivo_no_efectiva = "Renuente",
  // desde que simplificamos los botones de estado de la visita).
  const esRenuente = (v) => v.estado_vivienda === "Renuente" || (v.estado_vivienda === "No efectiva" && v.motivo_no_efectiva === "Renuente");

  // Renuente suma a las efectivas (se logró el contacto, aunque la
  // persona no quiso participar) — y ya no se considera pendiente de
  // revisitar (no tiene caso volver a tocar esa puerta).
  const efectivas = useMemo(() => porVivienda.filter(v => v.estado_vivienda === "Efectiva" || esRenuente(v)), [porVivienda]);

  // ── KPIs ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalViviendas = porVivienda.length;
    const totalEfectivas = efectivas.length;
    const pctEfectivas = totalViviendas ? Math.round((totalEfectivas / totalViviendas) * 100) : 0;
    const porRevisitar = porVivienda.filter(v => v.estado_vivienda === "Cerrada" || (v.estado_vivienda === "No efectiva" && (v.motivo_no_efectiva === "Cerrada" || v.motivo_no_efectiva === "Menor en casa"))).length;
    const eispEncontrados = porVivienda.filter(v => v.eisp_encontrado === "Si").length;
    const totalPersonas = efectivas.reduce((acc, v) => acc + aArray(v.integrantes).length, 0);
    const totalContactos = porVivienda.reduce((acc, v) => acc + aArray(v.contactos).length, 0);
    return { totalViviendas, totalEfectivas, pctEfectivas, porRevisitar, eispEncontrados, totalPersonas, totalContactos };
  }, [porVivienda, efectivas]);

  // ── Datos para gráficas ─────────────────────────────────────────────
  const porEstado = useMemo(() =>
    // Cada vivienda cae en una sola categoría — si es un registro nuevo
    // ("No efectiva" + motivo_no_efectiva), se cuenta por su motivo; si
    // es un registro viejo (estado_vivienda directo tipo "Cerrada"),
    // se cuenta tal cual.
    (() => {
      const conteo = { Efectiva: 0, Cerrada: 0, Renuente: 0, "No habitada": 0, Abandona: 0, "Menor en casa": 0 };
      porVivienda.forEach(v => {
        const categoria = v.estado_vivienda === "No efectiva" ? (v.motivo_no_efectiva || null) : v.estado_vivienda;
        if (categoria && conteo[categoria] !== undefined) conteo[categoria]++;
      });
      return Object.entries(conteo).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
    })(),
    [porVivienda]);

  const porTipoVivienda = useMemo(() => contarPor(porVivienda, "tipo_fisico_vivienda"), [porVivienda]);
  const porEnfoque = useMemo(() => contarPor(efectivas, "pertenencia_etnica"), [efectivas]);
  const porGenero = useMemo(() => contarPor(efectivas, "genero"), [efectivas]);
  const porEps = useMemo(() => contarPor(efectivas, "eps"), [efectivas]);

  const porEdad = useMemo(() => {
    const mapa = Object.fromEntries(ORDEN_EDAD.map(k => [k, 0]));
    efectivas.forEach(v => aArray(v.integrantes).forEach(p => { mapa[bucketEdad(p.edad, p.unidad)]++; }));
    return ORDEN_EDAD.map(name => ({ name, value: mapa[name] })).filter(d => d.value > 0);
  }, [efectivas]);

  const eispSiNo = useMemo(() => ([
    { name: "Sí", value: porVivienda.filter(v => v.eisp_encontrado === "Si").length },
    { name: "No", value: porVivienda.filter(v => v.eisp_encontrado === "No").length },
  ].filter(d => d.value > 0)), [porVivienda]);

  // Comunicación (solo viviendas efectivas que ya respondieron)
  const comunicacion = useMemo(() => {
    const conResp = efectivas.filter(v => v.sabe_vacunacion || v.conoce_personajes_vacunacion || aArray(v.medios_informacion).length);
    const medios = {};
    conResp.forEach(v => aArray(v.medios_informacion).forEach(m => { medios[m] = (medios[m] || 0) + 1; }));
    const siNo = (campo) => ({ si: conResp.filter(v => v[campo] === "Si").length, no: conResp.filter(v => v[campo] === "No").length });
    return {
      total: conResp.length,
      medios: Object.entries(medios).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
      sabe: siNo("sabe_vacunacion"),
      personajes: siNo("conoce_personajes_vacunacion"),
    };
  }, [efectivas]);

  const rankingEntrevistadores = useMemo(() => contarPor(visitas, "entrevistador").slice(0, 8), [visitas]);

  const visitasPorDia = useMemo(() => {
    const mapa = {};
    visitas.forEach(v => {
      const dia = v.createdAt?.split("T")[0];
      if (!dia) return;
      mapa[dia] = (mapa[dia] || 0) + 1;
    });
    return Object.entries(mapa).map(([name, value]) => ({ name, value })).sort((a, b) => a.name.localeCompare(b.name)).slice(-21);
  }, [visitas]);

  if (cargando) return <p className="text-muted">Cargando dashboard...</p>;

  if (!ctx?.territorio) {
    return <p className="text-muted">Selecciona un territorio para ver el dashboard.</p>;
  }

  if (!porVivienda.length) {
    return (
      <div style={{ ...CARD, textAlign: "center", padding: 40 }}>
        <p className="text-muted mb-0">Todavía no hay visitas registradas en {ctx.territorio}{ctx.comunidad ? ` · ${ctx.comunidad}` : ""}.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        <TarjetaKPI label="Viviendas visitadas" valor={kpis.totalViviendas} color={VERDE} icono="🏠" />
        <TarjetaKPI label="Efectivas" valor={`${kpis.totalEfectivas} (${kpis.pctEfectivas}%)`} color={VERDE_CLARO} icono="✅" />
        <TarjetaKPI label="Por revisitar" valor={kpis.porRevisitar} color={AMARILLO} icono="⏳" />
        <TarjetaKPI label="EISP encontrados" valor={kpis.eispEncontrados} color={ROJO} icono="🚨" />
        <TarjetaKPI label="Personas registradas" valor={kpis.totalPersonas} color={NARANJA} icono="👥" />
        <TarjetaKPI label="Contactos registrados" valor={kpis.totalContactos} color="#0C447C" icono="📇" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>

        {/* Estado de las viviendas */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Estado de las viviendas</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={porEstado} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                {porEstado.map((d, i) => <Cell key={i} fill={ESTADO_COLOR[d.name] || PALETA[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* EISP */}
        <div style={CARD}>
          <div style={TITULO_CARD}>¿EISP encontrado?</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={eispSiNo} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                <Cell fill={ROJO} />
                <Cell fill={VERDE_CLARO} />
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tipo de vivienda */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Tipo de vivienda</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porTipoVivienda} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill={VERDE} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Distribución por edad */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Distribución por edad (personas registradas)</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porEdad}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill={NARANJA} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Enfoque diferencial */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Enfoque diferencial</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porEnfoque} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill={VERDE_CLARO} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Género */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Género</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={porGenero} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                {porGenero.map((d, i) => <Cell key={i} fill={PALETA[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* EPS */}
        <div style={CARD}>
          <div style={TITULO_CARD}>EPS</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porEps} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#0C447C" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Ranking entrevistadores */}
        <div style={CARD}>
          <div style={TITULO_CARD}>Entrevistadores — más visitas realizadas</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={rankingEntrevistadores} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill={VERDE} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Comunicación */}
        {comunicacion.total > 0 && (
          <div style={{ ...CARD, gridColumn: "1 / -1" }}>
            <div style={TITULO_CARD}>Comunicación — {comunicacion.total} vivienda(s) efectivas que respondieron</div>
            <div className="d-flex flex-wrap gap-4">
              <div style={{ flex: "2 1 320px", minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>¿Cómo se entera de la información de la Secretaría?</div>
                <ResponsiveContainer width="100%" height={Math.max(160, comunicacion.medios.length * 34)}>
                  <BarChart data={comunicacion.medios} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 10.5 }} />
                    <Tooltip />
                    <Bar dataKey="value" name="Viviendas" fill={NARANJA} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: "1 1 220px" }}>
                {[["¿Sabe algo sobre vacunación?", comunicacion.sabe], ["¿Conoce los personajes (muñequitos) de la vacunación?", comunicacion.personajes]].map(([t, d]) => {
                  const tot = d.si + d.no;
                  const pct = tot ? Math.round((d.si / tot) * 100) : 0;
                  return (
                    <div key={t} className="mb-3">
                      <div style={{ fontSize: 12.5, fontWeight: 700 }}>{t}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: VERDE_CLARO }}>{pct}% <span style={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>SÍ ({d.si} de {tot})</span></div>
                      <div style={{ height: 8, background: "#f1e3e2", borderRadius: 6 }}>
                        <div style={{ width: `${pct}%`, height: 8, background: VERDE_CLARO, borderRadius: 6 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tendencia de visitas por día */}
        <div style={{ ...CARD, gridColumn: "1 / -1" }}>
          <div style={TITULO_CARD}>Visitas por día (últimos {visitasPorDia.length} días con actividad)</div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={visitasPorDia}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke={VERDE} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function TarjetaKPI({ label, valor, color, icono }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #DCE3DE", borderLeft: `4px solid ${color}`, padding: "14px 16px" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1.15 }}>{valor}</div>
      <div style={{ fontSize: 11.5, color: "#6c757d", fontWeight: 600 }}>{label}</div>
    </div>
  );
}